import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";
import PageLoadingSpinner from "components/common/PageLoader";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import { getGoodsReceipt } from "../services/GoodsReceiptService";
import type { GoodsReceipt } from "../types/goods-receipt.types";
import GoodsReceiptOverviewContext from "./GoodsReceiptOverviewContext";
import Section from "./Section";

export default function GoodsReceiptOverview() {
  document.title = getDocumentTitle("Goods Receipt Details");
  const { id } = useParams<{ id: string }>();
  const [goodsReceipt, setGoodsReceipt] = useState<GoodsReceipt | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!id) return;
    const res = await getGoodsReceipt(Number(id));
    if (res.success && res.data) setGoodsReceipt(res.data);
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getGoodsReceipt(Number(id));
        if (!cancelled && res.success && res.data) {
          setGoodsReceipt(res.data);
        } else if (!cancelled) {
          setGoodsReceipt(null);
        }
      } catch {
        if (!cancelled) setGoodsReceipt(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return <PageLoadingSpinner message="Loading goods receipt…" />;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Goods receipt details"
          pageTitle="Inventory Management"
          items={[
            {
              label: "Purchase orders",
              path: "/inventory-management/purchase-orders",
            },
          ]}
        />
        <GoodsReceiptOverviewContext.Provider
          value={{ goodsReceipt, refetch }}
        >
          <Section />
        </GoodsReceiptOverviewContext.Provider>
      </Container>
    </div>
  );
}
